export const ALTERNATIVE_TREE_PATH_SEPARATOR = ' / ';

export const ALTERNATIVE_TREE_VIEW_DEFAULT_LEVELS = {
  disabledInput: null,
  dynamicTable: 1,
  pdf: null,
  listExport: null,
  compactSummary: 1,
} as const;

export type AlternativeTreeViewKey = keyof typeof ALTERNATIVE_TREE_VIEW_DEFAULT_LEVELS;

export type AlternativeTreePregunta = {
  alternativeTree?: boolean;
  alternativeTreeDisplayLevels?: number | null;
  multipleChoiceOthers?: string;
};

export function resolveAlternativeTreeDisplayLevels(
  pregunta: AlternativeTreePregunta | null | undefined,
  viewKey: AlternativeTreeViewKey,
): number | null {
  const configured = pregunta?.alternativeTreeDisplayLevels;
  if (configured != null && configured > 0) {
    return Math.min(Math.floor(configured), 3);
  }
  return ALTERNATIVE_TREE_VIEW_DEFAULT_LEVELS[viewKey];
}

export function formatAlternativeTreePath(
  fullPath: string,
  levels: number | null,
  separator: string = ALTERNATIVE_TREE_PATH_SEPARATOR,
): string {
  if (!fullPath) return fullPath;
  if (levels == null || levels <= 0) return fullPath;

  const segments = fullPath.split(separator).filter((segment) => segment.length > 0);
  if (!segments.length) return fullPath;
  if (levels >= segments.length) return fullPath;

  return segments.slice(segments.length - levels).join(separator);
}

/**
 * Valor que se persiste al elegir la alternativa.
 * Un label sin caracteres de palabra (por ejemplo "*") se creaba con id ""
 * y el requerido lo trataba como respuesta vacía.
 */
export function alternativeSelectValue(alt: any): any {
  const raw = alt?.id ?? alt?.value;
  if (raw !== null && raw !== undefined && String(raw) !== '') return raw;
  const label = alt?.label != null ? String(alt.label).trim() : '';
  if (label) return label;
  return raw;
}

export function findMultipleChoiceTreeFullPath(
  list: any[] | null | undefined,
  val: any,
  separator: string = ALTERNATIVE_TREE_PATH_SEPARATOR,
): string | null {
  if (!list?.length || val === null || val === undefined || val === '') return null;

  for (const opt of list) {
    const optionValue = opt?.value ?? opt?.id;
    if (optionValue === val) return opt?.label ?? null;
    if (opt?.children?.length > 0) {
      const childPath = findMultipleChoiceTreeFullPath(opt.children, val, separator);
      if (childPath) {
        return `${opt.label}${separator}${childPath}`;
      }
    }
  }
  return null;
}

export function getMultipleChoiceTreeDisplayLabel(
  alternativas: any[] | null | undefined,
  selectedId: any,
  pregunta: AlternativeTreePregunta | null | undefined,
  viewKey: AlternativeTreeViewKey,
  findFullPathFn: (list: any[] | null | undefined, id: any) => string | null = findMultipleChoiceTreeFullPath,
): string | null {
  if (selectedId === 'others') {
    return null;
  }

  if (!pregunta?.alternativeTree) {
    return null;
  }

  const fullPath = findFullPathFn(alternativas, selectedId);
  if (!fullPath) return null;

  const levels = resolveAlternativeTreeDisplayLevels(pregunta, viewKey);
  return formatAlternativeTreePath(fullPath, levels);
}
